import { FC, useState } from 'react';
import { Button, Form, Modal, notification } from 'antd';
import { useLazyGetDivisionEmployeesQuery } from 'app/services/edpApiV1/viewer';
import { useCreateAbsenceMutation } from 'app/services/edpApiV1/absence';
import { TDivisionEmployees } from 'shared/types/types';
import { getCreateAbsenceDate } from 'shared/helpers/utils';
import { useNotifications } from 'shared/hooks/use-notifications';
import { TAbsenceForm } from 'shared/types/absence.types';
import { absenceSupervisorApi } from 'app/services/edpApiV1/supervisor/absenceSupervisor';
import { useAppSelector } from 'app/store';
import { getCurrentDivision } from 'entities/divisions/model';
import { TabKeys } from 'features/divisions';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import RangeInput from './rangeInput';
import EmployeeInput from './employeeInput';
import ModalComment from './modalComment';
import style from './index.module.scss';

type TProps = {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const ModalAddOneAbsence: FC<TProps> = ({ isModalOpen, setIsModalOpen }) => {
  const [form] = Form.useForm<TAbsenceForm>();
  const [employee, setEmployee] = useState<TDivisionEmployees | null>(null);
  const [range, setRange] = useState<string[] | null>(null);
  const [comment, setComment] = useState<string>('');

  const [getEmployees, resultEmployees] = useLazyGetDivisionEmployeesQuery();
  const [createAbsenceTrigger, createResult] = useCreateAbsenceMutation();
  const { isSuccess, isError, reset } = createResult;
  const isFullForm = employee && range && comment;
  const { currentOption } = useAppSelector(getCurrentDivision);

  const resetStates = () => {
    setEmployee(null);
    setRange(null);
    setComment('');
  };

  const [createAbsencesDeputy, { isLoading: isDeputyLoading }] = absenceSupervisorApi.useCreateAbsenceDeputyMutation();
  const [createAbsencesActing, { isLoading: isActingLoading }] = absenceSupervisorApi.useCreateAbsenceActingMutation();
  const [createAbsencesSupervisor, { isLoading: isSupervisorLoading }] =
    absenceSupervisorApi.useCreateAbsenceSupervisorMutation();
  const [createAbsencesTimekeeper, { isLoading: isTimekeeperLoading }] =
    absenceSupervisorApi.useCreateAbsenceTimekeeperMutation();

  const refetch = async (data: { employeeId: number; startedAt: string; endedAt: string; comment: string }) => {
    if (!currentOption) {
      return;
    }
    try {
      switch (currentOption.type) {
        case TabKeys.ACTING:
          await createAbsencesActing({ divisionId: currentOption.value, ...data }).unwrap();
          break;
        case TabKeys.DEPUTY:
          await createAbsencesDeputy({ divisionId: currentOption.value, ...data }).unwrap();
          break;
        case TabKeys.SUPERVISOR:
          await createAbsencesSupervisor(data).unwrap();
          break;
        case TabKeys.TIMEKEEPER:
          await createAbsencesTimekeeper(data).unwrap();
          break;
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Не удалось создать неявку') });
    }
  };

  const onChangeEmployee = () => {
    if (range) {
      form.setFieldsValue({
        period: undefined,
      });
      setRange(null);
    }
    if (comment) {
      form.setFieldsValue({
        comment: '',
      });
      setComment('');
    }
  };
  const onChangeRange = () => {
    if (comment) {
      form.setFieldsValue({
        comment: '',
      });
      setComment('');
    }
  };
  // функции для сброса зависимых полей, если значение меняется end
  const handleCancel = () => {
    form.resetFields();
    resetStates();
    setIsModalOpen(false);
  };

  const onFinish = async () => {
    try {
      if (range && employee) {
        const startedAt = getCreateAbsenceDate(range[0]);
        const endedAt = getCreateAbsenceDate(range[1]);
        const newAbsence = {
          employeeId: employee.id,
          startedAt,
          endedAt,
          comment,
        };
        await refetch(newAbsence);
        notification.success({ message: 'Неявка создана' });
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Ошибка при создании неявки') });
    }

    // очистка формы и сброс стейта
    handleCancel();
  };

  useNotifications(isSuccess, isError, 'Неявка успешно создана', 'Ошибка при создании неявки', reset);

  return (
    <Modal
      title="Заполните поля для создания неявки"
      footer={false}
      onCancel={handleCancel}
      visible={isModalOpen}
      bodyStyle={{ padding: '40px' }}
      width={700}
      destroyOnClose
    >
      <Form form={form} name="createOneAbsence" onFinish={onFinish} layout="vertical">
        <EmployeeInput
          data={resultEmployees.data}
          getEmployees={params => {
            params.divisionId = currentOption?.value;
            return getEmployees(params);
          }}
          setEmployee={setEmployee}
          onChangeEmployee={onChangeEmployee}
          divisionName={employee?.division}
        />
        <RangeInput disabled={!employee} setRange={setRange} onChangeRange={onChangeRange} />
        <ModalComment disabled={!range} setComment={setComment} />
        <Form.Item className={style.subBtn}>
          <Button
            htmlType="submit"
            type="primary"
            disabled={!isFullForm}
            loading={isDeputyLoading || isSupervisorLoading || isActingLoading || isTimekeeperLoading}
          >
            Создать
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ModalAddOneAbsence;
